import { useState } from "react";
import { authClient } from "../auth/client.js";

// E1.3.c — Salasanan vaihto. Käyttää better-auth:n built-in
// changePassword-endpointtia (POST /api/auth/change-password).
// Vaatii nykyisen salasanan + uuden 8+ merkkiä.

export function PasswordChangeModal(props: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (newPassword !== confirm) {
      setError("Uudet salasanat eivät täsmää.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Uuden salasanan pitää olla vähintään 8 merkkiä.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true, // turvallisuusparannus: muut sessiot loppuvat
      });
      if (res.error) {
        throw new Error(res.error.message ?? "Salasanan vaihto epäonnistui");
      }
      props.onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-6">
        <h4 className="text-lg font-medium text-slate-100">Vaihda salasana</h4>
        <p className="mt-1 text-sm text-slate-400">
          Muut kirjautuneet sessiot päättyvät automaattisesti uuden salasanan
          tallentamisen jälkeen.
        </p>

        <form onSubmit={onSubmit} className="mt-4 space-y-3">
          <label className="block text-xs text-slate-300">
            Nykyinen salasana
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
              className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>
          <label className="block text-xs text-slate-300">
            Uusi salasana (vähintään 8 merkkiä)
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>
          <label className="block text-xs text-slate-300">
            Vahvista uusi salasana
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>

          {error && (
            <p className="rounded border border-red-700 bg-red-950/40 px-3 py-2 text-xs text-red-200">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={props.onClose}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              Peruuta
            </button>
            <button
              type="submit"
              disabled={
                saving ||
                !currentPassword ||
                !newPassword ||
                !confirm ||
                newPassword !== confirm
              }
              className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {saving ? "Vaihdetaan…" : "Vaihda salasana"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
